import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Box } from '../Rebass';
import { AnimatePresence, motion } from '../Motion';
import Button from '../Button';
import useHasMounted from '../../hooks/useHasMounted';

interface VideoProps {
  fullUrl?: string;
  previewUrl?: string;
  renderThumbnail?: () => React.ReactNode;
  canPlay?: boolean;
  toggleModal?: (videoUrl: string) => void;
  isModalOpen?: boolean;
  visualRatio?: number;
}

const StyledVideo = styled.video`
  height: 100%;
  width: 100%;
  object-fit: cover;
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
`;

const ImagePreview = motion.custom(styled(Box)`
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 2;
  top: 0;
  left: 0;
`);

const PlayOverlay = styled(Box)`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;
`;

const Video = ({
  fullUrl,
  previewUrl,
  renderThumbnail,
  canPlay = true,
  toggleModal,
  isModalOpen,
  visualRatio,
}: VideoProps) => {
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const hasMounted = useHasMounted();

  let videoStyles: any = {};
  if (visualRatio) {
    videoStyles.paddingBottom = `${visualRatio}%`;
    videoStyles.position = 'relative';
  }

  if (!renderThumbnail && !previewUrl) {
    videoStyles.bg = 'brandBrown';
  }

  return (
    <Box height="100%" sx={videoStyles}>
      {renderThumbnail && (
        // @ts-ignore
        <AnimatePresence initial={false}>
          {(isModalOpen || !isPreviewReady) && (
            <ImagePreview
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              initial={{ opacity: 1 }}
            >
              {renderThumbnail()}
            </ImagePreview>
          )}
        </AnimatePresence>
      )}
      {hasMounted && previewUrl && (
        <StyledVideo
          autoPlay={true}
          loop={true}
          muted={true}
          controls={false}
          playsInline={true}
          onLoadedData={() => {
            if (canPlay) {
              setIsPreviewReady(true);
            }
          }}
        >
          <source src={previewUrl} type="video/mp4" />
        </StyledVideo>
      )}
      {fullUrl && canPlay && (
        <PlayOverlay onClick={() => toggleModal(fullUrl)}>
          <Button
            variant="icon"
            size="large"
            icon="play"
            colorScheme="transparent"
          />
        </PlayOverlay>
      )}
    </Box>
  );
};

export default Video;
